import googleFontCatalogData from './googleFontCatalog.json'

export interface GoogleFontCatalogEntry {
  label: string
  value: string
  category: string
  isGoogleFont: true
}

export interface CuratedFontOption {
  label: string
  value: string
  isGoogleFont: boolean
}

// Google's font "category" doesn't map 1:1 onto a CSS generic family — this is the closest fallback
// for each so a font-family declaration still degrades sensibly before the real font loads.
const CATEGORY_TO_GENERIC: Record<string, string> = {
  'sans-serif': 'sans-serif',
  serif: 'serif',
  display: 'sans-serif',
  handwriting: 'cursive',
  monospace: 'monospace'
}

function toCssFontFamily(family: string, category: string): string {
  const generic = CATEGORY_TO_GENERIC[category] ?? 'sans-serif'
  return `${family.toLowerCase()}, ${generic}`
}

/** The full current Google Fonts catalog (~1500+ families), sorted alphabetically by label. */
export const GOOGLE_FONT_CATALOG: GoogleFontCatalogEntry[] = (
  googleFontCatalogData as Array<{ family: string; category: string }>
).map(({ family, category }) => ({
  label: family,
  value: toCssFontFamily(family, category),
  category,
  isGoogleFont: true
}))

export const GOOGLE_FONT_CATALOG_BY_VALUE: Record<string, GoogleFontCatalogEntry> = GOOGLE_FONT_CATALOG.reduce(
  (acc, entry) => {
    acc[entry.value] = entry
    return acc
  },
  {} as Record<string, GoogleFontCatalogEntry>
)

// These aren't Google-hosted (see loadPresentationFonts.ts) but are the presets' long-standing
// defaults, so they stay pinned at the top of the picker instead of only being reachable by typing
// their exact name into the search box.
export const CURATED_FONT_OPTIONS: CuratedFontOption[] = [
  { label: 'Bitter', value: 'bitter, serif', isGoogleFont: true },
  { label: 'Cormorant', value: 'cormorant, serif', isGoogleFont: true },
  { label: 'Crimson Pro', value: 'crimson pro, serif', isGoogleFont: true },
  { label: 'Fraunces', value: 'fraunces, serif', isGoogleFont: true },
  { label: 'Georgia', value: 'georgia, serif', isGoogleFont: false },
  { label: 'Karla', value: 'karla, sans-serif', isGoogleFont: true },
  { label: 'Montserrat', value: 'montserrat, sans-serif', isGoogleFont: true },
  { label: 'Roboto', value: 'roboto, sans-serif', isGoogleFont: false },
  { label: 'Roboto Mono', value: 'roboto mono, sans-serif', isGoogleFont: true },
  { label: 'Spectral', value: 'spectral, serif', isGoogleFont: true },
  { label: 'Ubuntu', value: 'ubuntu, sans-serif', isGoogleFont: true },
  { label: 'Work Sans', value: 'work sans, sans-serif', isGoogleFont: true },
  { label: 'Zilla Slab', value: 'zilla slab, serif', isGoogleFont: true }
]

const MAX_SEARCH_RESULTS = 150

function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Substring-matches `query` against the full catalog's labels and caps the result count so a broad
 * query (e.g. a single common letter) can't mount thousands of picker rows at once.
 */
export function searchGoogleFontCatalog(query: string): GoogleFontCatalogEntry[] {
  const normalizedQuery = normalizeForSearch(query)
  if (!normalizedQuery) {
    return []
  }
  const results: GoogleFontCatalogEntry[] = []
  for (const entry of GOOGLE_FONT_CATALOG) {
    if (normalizeForSearch(entry.label).includes(normalizedQuery)) {
      results.push(entry)
      if (results.length >= MAX_SEARCH_RESULTS) {
        break
      }
    }
  }
  return results
}

/** Finds the catalog or curated entry matching a stored `fontFamily` value, falling back to a
 * synthetic option (derived from the raw value) for legacy/unknown values so the picker never
 * renders blank for data saved before this catalog existed. */
export function findFontOption(value: string): CuratedFontOption | undefined {
  if (!value) {
    return undefined
  }
  if (value === 'default') {
    return { label: 'Default', value: 'default', isGoogleFont: false }
  }
  if (value === 'alt') {
    return { label: 'Alt', value: 'alt', isGoogleFont: false }
  }
  const curated = CURATED_FONT_OPTIONS.find((option) => option.value === value)
  if (curated) {
    return curated
  }
  const catalogEntry = GOOGLE_FONT_CATALOG_BY_VALUE[value]
  if (catalogEntry) {
    return catalogEntry
  }
  const commaIndex = value.indexOf(',')
  const rawLabel = commaIndex > -1 ? value.substring(0, commaIndex) : value
  const label = rawLabel.replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
  return { label, value, isGoogleFont: true }
}
